import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  useTheme, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  useMediaQuery,
  InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { tokens } from '../../theme';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import { useNavigate } from "react-router-dom";
import useClassService from '../../api/services/ClassService';
import Table from '../../components/Table';
import Header from "../../components/Header";
import LoadingSpinner from '../../components/LoadingSpinner';
import DeleteDialog from '../Class/Delete_Class';
import EditDialog from '../Class/Edit_Class';
import CreateDialog from '../Class/Create_Class';


const ClassesToViewTimetable = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const user = getUserFromCookies();
  const role = user?.data?.role;
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const {
    classes,
    totalRecords,
    fetchClasses,
    deleteClass
  } = useClassService();

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [showAllClasses, setShowAllClasses] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedIdForEdit, setSelectedIdForEdit] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    loadClasses();
  }, [page, showAllClasses]);

  const loadClasses = async () => {
    setIsLoading(true);
    await fetchClasses(page, size, showAllClasses ? null : "ONGOING");
    setIsLoading(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedClassId(null);
    loadClasses();
  };

  const confirmDeleteClass = () => {
    if (selectedClassId) {
      deleteClass(selectedClassId, () => {
        handleDeleteDialogClose();
        loadClasses();
      });
    }
  };


  const openClassTimetable = (selectedClassId) => {
    navigate(`/classTimetable/${selectedClassId}`);
  };

  // Filter classes based on search query
  const filteredClasses = useMemo(() => {
    if (!classes || classes.length === 0) return [];
    
    if (!searchQuery.trim()) return classes;
    
    const query = searchQuery.toLowerCase();
    return classes.filter((clazz) => {
      const className = (clazz.className || '').toLowerCase();
      const classTitle = (clazz.classTitle || '').toLowerCase();
      const classType = (clazz.classType || '').toLowerCase();
      const status = (clazz.status || '').toLowerCase();
      
      return className.includes(query) || 
             classTitle.includes(query) || 
             classType.includes(query) ||
             status.includes(query);
    });
  }, [classes, searchQuery]);

  // Base columns (visible to all)
  const columns = [
    { field: "className", headerName: "Class_Name", flex: 1, minWidth: 130 },
    { field: "classTitle", headerName: "Class_Title", flex: 1, minWidth: 130 },
    { field: "classType", headerName: "Class_type", flex: 1, minWidth: 130 },
    { field: "createdAt", headerName: "CreatedAt", flex: 1, minWidth: 150 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 100 },
    {
      field: "viewStudents",
      headerName: "View Students",
      width: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={() => openClassTimetable(row.id)}
          >
            VIEW TIMETABLE
          </Button>
        </Box>
      ),
    },
  ];

  
  return (
    <Box m="0px">
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={confirmDeleteClass}
        classId={selectedClassId}
      />

      <CreateDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={loadClasses}
      />

      <EditDialog
        classId={selectedIdForEdit}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={loadClasses}
      />

      <Box p={2} ml={2} mr={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={isSmallScreen ? 'column' : 'row'} gap={2}>
          <Header title="VIEW YOUR SESSIONS OR CHOOSE CLASS TO VIEW ITS TIMETABLE" />
          {(role === 'TEACHER' || role === 'ACADEMIC') && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate('/allSessionsByTeacherId')}
              sx={{
                minWidth: isSmallScreen ? '100%' : '200px',
                fontSize: isSmallScreen ? '0.875rem' : '1rem'
              }}
              fullWidth={isSmallScreen}
            >
              VIEW YOUR SESSIONS ONLY
            </Button>
          )}
        </Box>

        {/* Search Field - Only on small screens */}
        {isSmallScreen && (
          <Box sx={{ mb: 2, mt: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search classes by name, title, type, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: 1,
              }}
            />
          </Box>
        )}

        {isLoading ? (
          <LoadingSpinner message="Loading classes..." height="300px" />
        ) : isSmallScreen ? (
          // Card view for small screens
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredClasses.length > 0 ? (
              filteredClasses.map((clazz) => (
                <Card
                  key={clazz.id}
                  sx={{
                    border: `1px solid ${colors.grey[300]}`,
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: colors.primary[400],
                  }}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {/* Main row: Class Name and Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                            {clazz.classTitle || 'N/A'}
                          </Typography>
                          
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography 
                            variant="outlined" 
                            sx={{ 
                              fontSize: '0.65rem',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                              display: 'inline-block'
                            }}
                          >
                            {clazz.status || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Secondary row: Type and Created At */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption"  fontWeight="bold" color="textSecondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.25 }}>
                            {clazz.className || 'N/A'}
                          </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          {clazz.classType || 'N/A'}
                        </Typography>

                        
                      </Box>
                      
                      {/* Button */}
                      <Box sx={{ mt: 0.5 }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          size="small"
                          onClick={() => openClassTimetable(clazz.id)}
                          sx={{ fontSize: '0.75rem', py: 0.5, minHeight: '32px' }}
                        >
                          VIEW TIMETABLE
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No classes found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {searchQuery ? 'Try adjusting your search query' : 'No classes available'}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Table view for large screens
          <Table
            rows={classes}
            columns={columns}
            totalRecords={totalRecords}
            size={size}
            page={page}
            handlePageChange={handlePageChange}
            colors={colors}
          />
        )}
      </Box>
    </Box>
  );
};

export default ClassesToViewTimetable;
